// src/components/chatbot/ChatbotSessionsPage.tsx
// Track E8 — live sessions inbox + handoff control.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, Bot, CheckCircle, Clock, Inbox, Loader2, Phone,
  RefreshCw, Search, UserCog, XCircle,
} from 'lucide-react';

import {
  ApiError,
  handoffSession, listSessions,
  type Channel, type ChatbotSession, type SessionState,
} from '@/lib/api_chatbot';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props { addNotification?: Notifier }

const STATE_META: Record<SessionState, { label: string; cls: string; icon: JSX.Element }> = {
  active:    { label: 'Activa',    cls: 'bg-emerald-500/20 text-emerald-300', icon: <Loader2 size={12} className="animate-spin" /> },
  completed: { label: 'Completa',  cls: 'bg-sky-500/20 text-sky-300',          icon: <CheckCircle size={12} /> },
  abandoned: { label: 'Abandonada',cls: 'bg-zinc-500/20 text-zinc-300',        icon: <Clock size={12} /> },
  handoff:   { label: 'Handoff',   cls: 'bg-amber-500/20 text-amber-300',      icon: <UserCog size={12} /> },
};


export function ChatbotSessionsPage({ addNotification }: Props) {
  const [rows, setRows] = useState<ChatbotSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState<'all' | SessionState>('active');
  const [channelFilter, setChannelFilter] = useState<'all' | Channel>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ChatbotSession | null>(null);
  const [busy, setBusy] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await listSessions({
        state: stateFilter === 'all' ? undefined : stateFilter,
        channel: channelFilter === 'all' ? undefined : channelFilter,
        q: search || undefined,
        limit: 100,
      });
      setRows(out.items);
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se cargaron sesiones',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setLoading(false);
    }
  }, [stateFilter, channelFilter, search, addNotification]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Auto-poll active sessions every 10 s.
  useEffect(() => {
    if (stateFilter !== 'active' && !rows.some((r) => r.state === 'active')) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [stateFilter, rows, load]);

  const counts = useMemo(() => {
    const acc: Record<string, number> = { all: rows.length };
    for (const r of rows) acc[r.state] = (acc[r.state] || 0) + 1;
    return acc;
  }, [rows]);

  const onHandoff = async (session: ChatbotSession) => {
    const reason = window.prompt('Razón del handoff (opcional):') ?? '';
    setBusy((b) => ({ ...b, [session.id]: true }));
    try {
      const updated = await handoffSession(session.id, reason);
      setRows((r) => r.map((x) => (x.id === session.id ? updated : x)));
      if (selected && selected.id === session.id) setSelected(updated);
      addNotification?.({ type: 'success', title: 'Handoff marcado' });
    } catch (e: any) {
      addNotification?.({
        type: 'error',
        title: 'No se pudo',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[session.id]; return n; });
    }
  };

  return (
    <div className="flex gap-4 h-[calc(100vh-180px)] min-h-[500px]">
      {/* Left — filters + list */}
      <aside className="w-96 bg-gray-800/50 border border-gray-700 rounded-lg flex flex-col overflow-hidden">
        <div className="p-3 border-b border-gray-700 flex items-center gap-2">
          <Inbox size={18} className="text-emerald-400" />
          <h3 className="text-white font-semibold text-sm">Sesiones</h3>
          <button onClick={load} className="ml-auto text-gray-400 hover:text-white">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="p-2 border-b border-gray-700 space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por teléfono"
              className="w-full pl-8 pr-3 py-1.5 bg-gray-900 border border-gray-700 rounded-md text-white placeholder-gray-500 text-sm focus:border-emerald-500 focus:outline-none"
            />
          </div>
          <div className="flex gap-1 overflow-x-auto">
            {(['all', 'active', 'completed', 'handoff', 'abandoned'] as const).map((id) => (
              <button
                key={id}
                onClick={() => setStateFilter(id as any)}
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${
                  stateFilter === id
                    ? 'bg-white/10 border-white/20 text-white'
                    : 'bg-transparent border-transparent text-gray-400 hover:text-gray-200 hover:bg-white/5'
                }`}
              >
                {id === 'all' ? 'Todas' : STATE_META[id].label}
                <span className="px-1 rounded-full bg-black/20">{counts[id] ?? 0}</span>
              </button>
            ))}
          </div>
          <select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value as any)}
            className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded-md text-white text-xs"
          >
            <option value="all">Todos los canales</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="messenger">Messenger</option>
            <option value="web">Web</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && rows.length === 0 ? (
            <div className="p-6 text-center"><Loader2 className="inline animate-spin text-emerald-400" /></div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">Sin sesiones.</div>
          ) : rows.map((s) => {
            const m = STATE_META[s.state];
            return (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                className={`w-full text-left px-3 py-2 border-b border-gray-800 hover:bg-white/[0.04] ${
                  selected?.id === s.id ? 'bg-white/[0.06]' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white font-mono truncate">{s.contact_ref}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                  <Phone size={10} /> {s.channel}
                  {s.flow_id && <span>· flow #{s.flow_id}</span>}
                  {s.last_inbound_at && <span>· {timeAgo(s.last_inbound_at)}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Right — detail */}
      <section className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg flex flex-col overflow-hidden">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Selecciona una sesión.
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <div className="font-semibold text-white flex items-center gap-2">
                  <Bot size={16} className="text-emerald-400" />
                  <span className="font-mono">{selected.contact_ref}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1 flex gap-3">
                  <span>{selected.channel}</span>
                  {selected.flow_id && <span>flow #{selected.flow_id}</span>}
                  <span className={`px-2 py-0.5 rounded-full text-xs ${STATE_META[selected.state].cls}`}>
                    {STATE_META[selected.state].label}
                  </span>
                  <span>paso {selected.current_step_index}</span>
                </div>
              </div>
              {selected.state === 'active' && (
                <button
                  onClick={() => onHandoff(selected)}
                  disabled={busy[selected.id]}
                  className="o-btn o-btn-sm o-btn-primary flex items-center gap-1"
                >
                  {busy[selected.id] ? <Loader2 size={14} className="animate-spin" /> : <UserCog size={14} />}
                  Tomar control
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <SessionDetail session={selected} />
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function SessionDetail({ session }: { session: ChatbotSession }) {
  return (
    <>
      {session.handoff_reason && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-200">
          <span className="font-semibold">Handoff:</span> {session.handoff_reason}
        </div>
      )}

      <div>
        <div className="text-xs text-gray-400 uppercase mb-2">Timeline</div>
        <ul className="space-y-1 text-sm">
          <li className="flex gap-2 text-gray-300">
            <span className="text-gray-500 w-28 shrink-0">Creada</span>
            <span>{new Date(session.created_at).toLocaleString('es-MX')}</span>
          </li>
          {session.last_inbound_at && (
            <li className="flex gap-2 text-gray-300">
              <span className="text-gray-500 w-28 shrink-0">Último inbound</span>
              <span>{new Date(session.last_inbound_at).toLocaleString('es-MX')}</span>
            </li>
          )}
          {session.last_outbound_at && (
            <li className="flex gap-2 text-gray-300">
              <span className="text-gray-500 w-28 shrink-0">Último outbound</span>
              <span>{new Date(session.last_outbound_at).toLocaleString('es-MX')}</span>
            </li>
          )}
          {session.completed_at && (
            <li className="flex gap-2 text-gray-300">
              <span className="text-gray-500 w-28 shrink-0">Cerrada</span>
              <span>{new Date(session.completed_at).toLocaleString('es-MX')}</span>
            </li>
          )}
        </ul>
      </div>

      <div>
        <div className="text-xs text-gray-400 uppercase mb-2">Context data</div>
        {Object.keys(session.context_data || {}).length === 0 ? (
          <p className="text-sm text-gray-500">— vacío —</p>
        ) : (
          <pre className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs text-gray-200 whitespace-pre-wrap font-mono">
{JSON.stringify(session.context_data, null, 2)}
          </pre>
        )}
      </div>
    </>
  );
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - Date.parse(iso)) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString('es-MX');
}

export default ChatbotSessionsPage;
